/**
 * FaqPage - FAQ 中心页（/:lang/faq）。
 *
 * 内容存 i18n（faq 命名空间，四语对齐——FAQ 是通用产品问题，值得翻译）。
 * 三组分类（产品 / 定价授权 / 技术打印），复用 .pricing-faq 折叠样式。
 * FAQPage JSON-LD 由 Seo 组件在 faq 路由注入。
 *
 * 注意：FAQ 内容页四语都提供（不像 guides/use-cases 仅英文），
 * 故不用 EnglishOnlyRoute 守卫。
 */

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import PublicLayout from "../components/PublicLayout";
import { Plus, Minus } from "../components/icons";
import { upsertJsonLd, removeJsonLd } from "../lib/jsonld";
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from "../lib/support";
import { LocalizedLink } from "../lib/lang";

/** FAQ 分组定义：组 key → 该组下的问题 key 列表。 */
const FAQ_GROUPS: { group: "product" | "pricing" | "printing"; keys: string[] }[] = [
  { group: "product", keys: ["whatIsTsuvi", "howDifferent", "inputTypes", "howLong"] },
  { group: "pricing", keys: ["freeToUse", "firstMonthIntro", "commercial", "credits", "plazaCredits", "teamPlans"] },
  { group: "printing", keys: ["formats", "printReady", "printerSupport", "repairNeeded"] },
];

export default function FaqPage() {
  const { t } = useTranslation();

  // 注入 FAQPage JSON-LD（用独立 id faq-center，避免与 Seo 组件落地页的 faq 块冲突）。
  // 注意：Seo 组件在非落地页会 removeJsonLd("faq")，但那是另一个 id，不影响本块。
  useEffect(() => {
    const mainEntity = FAQ_GROUPS.flatMap((g) =>
      g.keys.map((k) => ({
        "@type": "Question",
        name: t(`faq.items.${k}.q`),
        acceptedAnswer: { "@type": "Answer", text: t(`faq.items.${k}.a`) },
      })),
    );
    upsertJsonLd("faq-center", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity,
    });
    return () => removeJsonLd("faq-center");
  }, [t]);

  return (
    <PublicLayout>
      <section className="faq-page">
        <span className="eyebrow">{t("content.faq.title")}</span>
        <h1 className="section-title">{t("content.faq.title")}</h1>
        {/* 副标题即客服联系行（Creem MoR 合规：公开页面可见客服邮箱） */}
        <p className="section-subtitle">
          {t("faq.subtitleContact")}{" "}
          <a href={SUPPORT_MAILTO}>{SUPPORT_EMAIL}</a>
        </p>

        {FAQ_GROUPS.map(({ group, keys }) => (
          <div key={group} className="faq-group">
            <h2 className="faq-group-title">{t(`faq.groups.${group}`)}</h2>
            {keys.map((k) => (
              <details key={k}>
                <summary>
                  <span>{t(`faq.items.${k}.q`)}</span>
                  <Plus size={16} className="faq-toggle-icon faq-toggle-plus" />
                  <Minus size={16} className="faq-toggle-icon faq-toggle-minus" />
                </summary>
                <p>{t(`faq.items.${k}.a`)}</p>
              </details>
            ))}
          </div>
        ))}

        {/* 底部回首页（复用 .link-btn，与文章页返回链接一致） */}
        <div className="faq-back-home">
          <LocalizedLink to="/" className="link-btn">
            {t("common.backHome")}
          </LocalizedLink>
        </div>
      </section>
    </PublicLayout>
  );
}
